import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  IonBadge,
  IonButton,
  IonContent,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonPage,
  IonText,
  useIonToast,
} from '@ionic/react';

import { useHistory, useLocation } from 'react-router-dom';
import { Tabs, Tab } from '@material-ui/core';

import Header from 'src/components/Header';
import DatePicker from 'src/components/DatePicker';

import { GestionErroresAPI } from 'src/services/gestionErrores';
import { ERROR_UI } from 'src/modules/gestionErrores/constantes';
import { GestionErroresResumenItem, TipoError } from 'src/modules/gestionErrores/types';

import { contarLpGestionadosParaPs } from 'src/modules/gestionErrores/gestionLpStorage';

import PendientesPanel from 'src/modules/gestionMarcacionV2/components/PendientesPanel';
import ProcesadasPanel from 'src/modules/gestionMarcacionV2/components/ProcesadasPanel';
import {
  useGestionMarcacionApi,
  OperarioItem,
  UsuarioInfo,
} from 'src/modules/gestionMarcacionV2/hooks/useGestionMarcacionApi';

// Pestañas del módulo unificado. "Pendientes" y "Procesadas" son el ex módulo
// "Gestión Marcación (nuevo)"; se puede entrar directo con ?tab=pendientes|procesadas.
const TABS = ['ps', 'pendientes', 'procesadas'] as const;

const tabDesdeSearch = (search: string): number => {
  try {
    const valor = new URLSearchParams(search || '').get('tab');
    const idx = TABS.indexOf(valor as typeof TABS[number]);
    return idx >= 0 ? idx : 0;
  } catch {
    return 0;
  }
};

/**
 * Formatea una fecha a ISO (YYYY-MM-DD) usando el calendario local.
 * Importante: evitamos `toISOString()` para no introducir desfases por zona horaria.
 */
const formatDateToISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const computePrioridad = (
  item: GestionErroresResumenItem,
  lpGestionadosSinAsignacion: number
): number => {
  const entries = Object.entries(item.por_tipo || {}) as Array<[TipoError, number]>;

  // ALERTA_TOLERANCIA se excluye de la prioridad porque no se gestiona y suele generar ruido.
  // Además, SIN-ASIGNACION gestionado por LP se descuenta para que no eleve prioridad.
  const presentes = entries
    .map(([t, n]) => {
      if (t === 'ALERTA_TOLERANCIA') return [t, 0] as [TipoError, number];
      if (t === 'SIN-ASIGNACION') return [t, Math.max(0, (n || 0) - (lpGestionadosSinAsignacion || 0))] as [TipoError, number];
      return [t, n || 0] as [TipoError, number];
    })
    .filter(([_, n]) => (n || 0) > 0)
    .map(([t]) => ERROR_UI[t].prioridad);

  if (!presentes.length) return 999;
  return Math.min(...presentes);
};

/**
 * Total visible/gestionable en el resumen.
 *
 * Reglas:
 * - No contamos ALERTA_TOLERANCIA porque no se gestiona.
 * - No contamos SIN-ASIGNACION ya gestionado por LP (marca local) para que deje de sumar como error.
 */
const computeTotalGestionable = (
  item: GestionErroresResumenItem,
  lpGestionadosSinAsignacion: number
): number => {
  const total = item.total || 0;
  const alertas = item.por_tipo?.ALERTA_TOLERANCIA || 0;
  const lp = lpGestionadosSinAsignacion || 0;
  return Math.max(0, total - alertas - lp);
};

// Clave en sessionStorage: recordar filtro mientras el usuario navega dentro del módulo.
// Importante: se limpia al salir a otros módulos para que, al re-entrar, vuelva a “hoy”.
const STORAGE_KEY_GESTION_ERRORES_FILTRO = 'gestion_errores:filtro:ultimo';

const parseRangoFromSearch = (search: string): { desde?: string; hasta?: string } => {
  try {
    const params = new URLSearchParams(search || '');
    const desde = params.get('desde') || undefined;
    const hasta = params.get('hasta') || undefined;
    return { desde, hasta };
  } catch {
    return {};
  }
};

const dateFromISO = (iso: string): Date | undefined => {
  const txt = String(iso || '').trim();
  if (!txt) return undefined;
  // Aseguramos parse en zona local para evitar desfase.
  const d = new Date(`${txt}T00:00:00`);
  if (Number.isNaN(d.getTime())) return undefined;
  return d;
};

const leerFiltroStorage = (): { desde?: string; hasta?: string } | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY_GESTION_ERRORES_FILTRO);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      desde: typeof parsed.desde === 'string' && parsed.desde ? parsed.desde : undefined,
      hasta: typeof parsed.hasta === 'string' && parsed.hasta ? parsed.hasta : undefined,
    };
  } catch {
    return null;
  }
};

const guardarFiltroStorage = (desde?: string, hasta?: string) => {
  try {
    sessionStorage.setItem(
      STORAGE_KEY_GESTION_ERRORES_FILTRO,
      JSON.stringify({ desde: desde || null, hasta: hasta || null })
    );
  } catch {
    // No bloquear si falla el storage.
  }
};

const GestionErrores: React.FC = () => {
  const [present] = useIonToast();
  const history = useHistory();
  const location = useLocation();

  // DatePicker devuelve Date; el backend espera YYYY-MM-DD, que convertimos al buscar.
  const [fechaDesde, setFechaDesde] = useState<Date | undefined>(undefined);
  const [fechaHasta, setFechaHasta] = useState<Date | undefined>(undefined);

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<GestionErroresResumenItem[]>([]);
  const [rangoTexto, setRangoTexto] = useState<string>('');
  // Guardamos el último filtro aplicado para navegar al detalle sin pedirlo de nuevo.
  const [ultimoFiltro, setUltimoFiltro] = useState<{ desde?: string; hasta?: string }>({});

  // Pestaña activa (se lee una sola vez de la URL al entrar).
  const [tabActivo, setTabActivo] = useState<number>(() => tabDesdeSearch(location.search));
  // Las pestañas ya abiertas quedan montadas para no perder sus filtros al cambiar.
  const [tabsVisitados, setTabsVisitados] = useState<Record<number, boolean>>(() => ({ [tabDesdeSearch(location.search)]: true }));

  // Catálogos de Pendientes/Procesadas: se cargan recién al abrir una de esas pestañas.
  const apiMarcacion = useGestionMarcacionApi();
  const [catalogosCargados, setCatalogosCargados] = useState(false);
  const [puntosServicio, setPuntosServicio] = useState<string[]>([]);
  const [operarios, setOperarios] = useState<OperarioItem[]>([]);
  const [usuario, setUsuario] = useState<UsuarioInfo | null>(null);

  useEffect(() => {
    if (tabActivo === 0 || catalogosCargados) return;
    setCatalogosCargados(true);
    (async () => {
      const [usuarioRes, psRes, opRes] = await Promise.all([
        apiMarcacion.obtenerUsuario(),
        apiMarcacion.listarPuntosServicio(),
        apiMarcacion.listarOperarios(),
      ]);
      if (usuarioRes.ok && usuarioRes.data) setUsuario(usuarioRes.data);
      if (psRes.ok && psRes.data) setPuntosServicio(psRes.data);
      if (opRes.ok && opRes.data) setOperarios(opRes.data);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabActivo]);

  const resumenPedido = useRef(false);

  const cambiarTab = (idx: number) => {
    if (idx === 0 && !resumenPedido.current) {
      resumenPedido.current = true;
      const fromStorage = leerFiltroStorage() || {};
      const hoyISO = formatDateToISO(new Date());
      fetchData(fromStorage.desde || hoyISO, fromStorage.hasta || hoyISO);
    }
    setTabActivo(idx);
    setTabsVisitados((prev) => ({ ...prev, [idx]: true }));
  };

  const filtroActivo = useMemo(() => {
    // Preferimos la URL (navegación volver/forward); si no existe, usamos el último filtro aplicado.
    const fromUrl = parseRangoFromSearch(location.search);
    return {
      desde: fromUrl.desde || ultimoFiltro.desde,
      hasta: fromUrl.hasta || ultimoFiltro.hasta,
    };
  }, [location.search, ultimoFiltro.desde, ultimoFiltro.hasta]);

  const lpGestionadosEnRango = useMemo(() => {
    // Cache por PS para no recalcular por cada render.
    const map = new Map<number, number>();
    for (const ps of items || []) {
      const n = contarLpGestionadosParaPs(ps.ps_id, filtroActivo.desde, filtroActivo.hasta);
      map.set(ps.ps_id, n);
    }
    return map;
  }, [items, filtroActivo.desde, filtroActivo.hasta]);

  const fetchData = async (desde?: string, hasta?: string) => {
    try {
      setLoading(true);
      const data = await GestionErroresAPI.resumen(desde, hasta);
      setItems(data.datos || []);
      const r = data.rango;
      setRangoTexto(
        `Rango efectivo: ${r.fecha_desde_efectiva} a ${r.fecha_hasta_efectiva}`
      );
      // Persistimos el filtro solicitado (no el clamp) para que el detalle cargue lo mismo.
      setUltimoFiltro({ desde, hasta });
      guardarFiltroStorage(desde, hasta);
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'No se pudo cargar Gestión de Errores.';
      present({ message: String(msg), duration: 3500, color: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Al abrir el módulo:
    // 1) Si la URL trae desde/hasta, respetarlo (vuelve desde detalle).
    // 2) Si no hay query, intentar recuperar filtro de sessionStorage (navegación interna).
    // 3) Si no hay nada, inicializar en “hoy”.
    const fromUrl = parseRangoFromSearch(location.search);
    const fromStorage = leerFiltroStorage() || {};
    const hoyISO = formatDateToISO(new Date());

    const desdeInit = fromUrl.desde || fromStorage.desde || hoyISO;
    const hastaInit = fromUrl.hasta || fromStorage.hasta || hoyISO;

    setFechaDesde(dateFromISO(desdeInit));
    setFechaHasta(dateFromISO(hastaInit));

    // Mantener el querystring sincronizado ayuda a que el “volver” conserve el filtro.
    history.replace({ pathname: '/gestion-errores', search: `?desde=${desdeInit}&hasta=${hastaInit}` });
    // Si se entra directo a Pendientes/Procesadas, el resumen se pide recién al abrir "Por PS".
    if (tabActivo === 0) {
      resumenPedido.current = true;
      fetchData(desdeInit, hastaInit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Si el usuario sale del módulo (a otra ruta), limpiamos el filtro para que
    // al volver a entrar a Gestión de Errores arranque nuevamente en “hoy”.
    const unlisten = history.listen((nextLocation) => {
      const path = String(nextLocation.pathname || '');
      if (!path.startsWith('/gestion-errores')) {
        try {
          sessionStorage.removeItem(STORAGE_KEY_GESTION_ERRORES_FILTRO);
        } catch {
          // No bloquear.
        }
      }
    });
    return () => {
      try {
        unlisten();
      } catch {
        // No bloquear.
      }
    };
  }, [history]);

  const itemsOrdenados = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      const pa = computePrioridad(a, lpGestionadosEnRango.get(a.ps_id) || 0);
      const pb = computePrioridad(b, lpGestionadosEnRango.get(b.ps_id) || 0);
      if (pa !== pb) return pa - pb;
      return (a.ps_nombre || '').localeCompare(b.ps_nombre || '');
    });
    return copy;
  }, [items, lpGestionadosEnRango]);

  const onBuscar = async () => {
    if ((fechaDesde && !fechaHasta) || (!fechaDesde && fechaHasta)) {
      present({
        message: 'Debe completar fecha_desde y fecha_hasta.',
        duration: 3000,
        color: 'warning',
      });
      return;
    }

    const desde = fechaDesde ? formatDateToISO(fechaDesde) : undefined;
    const hasta = fechaHasta ? formatDateToISO(fechaHasta) : undefined;

    // Sincronizamos URL para mantener el filtro al navegar a detalle y volver.
    if (desde && hasta) {
      history.replace({ pathname: '/gestion-errores', search: `?desde=${desde}&hasta=${hasta}` });
    } else {
      history.replace({ pathname: '/gestion-errores', search: '' });
    }
    await fetchData(desde, hasta);
  };

  return (
    <IonPage>
      {/* Usamos el Header estándar para mantener barra superior roja y botón de menú */}
      <Header titulo="Gestión de Errores" />

      {/*
        Importante: este id debe coincidir con el contentId que se pasa al componente Menu
        en App.tsx (contentId="gestion-errores"). Si no coincide, el menú lateral no abre.
      */}
      <IonContent id="gestion-errores" fullscreen>
        <IonLoading isOpen={loading} message="Cargando..." />

        <Tabs
          value={tabActivo}
          onChange={(_e, val) => cambiarTab(val)}
          variant="fullWidth"
          TabIndicatorProps={{ style: { backgroundColor: '#900533' } }}
          style={{ background: 'white', borderBottom: '1px solid #eee' }}
        >
          <Tab label="Por PS" style={{ color: tabActivo === 0 ? '#900533' : '#666', minWidth: 0 }} />
          <Tab label="Pendientes" style={{ color: tabActivo === 1 ? '#900533' : '#666', minWidth: 0 }} />
          <Tab label="Procesadas" style={{ color: tabActivo === 2 ? '#900533' : '#666', minWidth: 0 }} />
        </Tabs>

        {tabsVisitados[1] && (
          <div className="gmv2-container" style={{ display: tabActivo === 1 ? undefined : 'none' }}>
            <PendientesPanel puntosServicio={puntosServicio} operarios={operarios} usuario={usuario} />
          </div>
        )}

        {tabsVisitados[2] && (
          <div className="gmv2-container" style={{ display: tabActivo === 2 ? undefined : 'none' }}>
            <ProcesadasPanel puntosServicio={puntosServicio} operarios={operarios} />
          </div>
        )}

        {tabActivo === 0 && (
        <>
        <IonList>
          <IonItem>
            <IonLabel>
              <div style={{ fontWeight: 700 }}>Rango</div>
              <div style={{ fontSize: 12, color: '#666' }}>
                Seleccione fechas desde el calendario.
              </div>
            </IonLabel>
          </IonItem>

          <IonItem lines="none">
            <div style={{ width: '100%', marginTop: 10 }}>
              <DatePicker
                placeholder="Fecha desde"
                value={fechaDesde}
                setValue={setFechaDesde}
              />
            </div>
          </IonItem>

          <IonItem lines="none">
            <div style={{ width: '100%', marginTop: 10 }}>
              <DatePicker
                placeholder="Fecha hasta"
                value={fechaHasta}
                setValue={setFechaHasta}
              />
            </div>
          </IonItem>

          <IonItem lines="none">
            <IonButton expand="block" onClick={onBuscar}>
              Buscar
            </IonButton>
          </IonItem>

          {rangoTexto ? (
            <IonItem lines="none">
              <IonText color="medium">{rangoTexto}</IonText>
            </IonItem>
          ) : null}
        </IonList>

        <IonList>
          {itemsOrdenados.map((ps) => (
            <IonItem
              key={ps.ps_id}
              routerLink={(() => {
                // Preferimos el querystring actual (si existe). Si no, caemos al último filtro aplicado.
                const currentSearch = String(location.search || '');
                if (currentSearch) return `/gestion-errores/${ps.ps_id}${currentSearch}`;

                const params = new URLSearchParams();
                if (ultimoFiltro.desde) params.set('desde', ultimoFiltro.desde);
                if (ultimoFiltro.hasta) params.set('hasta', ultimoFiltro.hasta);
                const qs = params.toString();
                return qs ? `/gestion-errores/${ps.ps_id}?${qs}` : `/gestion-errores/${ps.ps_id}`;
              })()}
              detail
            >
              <IonLabel>
                <div style={{ fontWeight: 700 }}>{ps.ps_nombre}</div>
                <div style={{ fontSize: 12, color: '#666' }}>Código: {ps.ps_codigo || '-'}</div>
              </IonLabel>

              <IonBadge color="primary">{computeTotalGestionable(ps, lpGestionadosEnRango.get(ps.ps_id) || 0)}</IonBadge>
            </IonItem>
          ))}

          {!loading && itemsOrdenados.length === 0 ? (
            <IonItem lines="none">
              <IonText color="medium">Sin inconsistencias en el rango.</IonText>
            </IonItem>
          ) : null}
        </IonList>
        </>
        )}
      </IonContent>
    </IonPage>
  );
};

export default GestionErrores;
